import { Input } from '../../../packages/shared/form-feedback/Native';
import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

export default function Signature() {
  const { dispatchId } = useParams();
  const navigate = useNavigate();
  const { fetchApi, error: apiError } = useApi();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerRole, setSignerRole] = useState<'customer' | 'driver' | 'courier'>('customer');
  const [saving, setSaving] = useState(false);
  const [gpsLocation, setGpsLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    // Get GPS location
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          // GPS not available or denied
        }
      );
    }

    // Set up canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }, []);

  const getCoords = (e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    setHasSignature(true);

    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature || !signerName) return;

    setSaving(true);

    // Convert canvas to base64
    const signatureData = canvas.toDataURL('image/png');

    const result = await fetchApi(`/picking/signature`, {
      method: 'POST',
      body: JSON.stringify({
        order_id: Number(dispatchId), signature_data: signatureData,
        signer_name: signerName, type: signerRole === 'customer' ? 'customer' : 'driver',
        latitude: gpsLocation?.lat || null,
        longitude: gpsLocation?.lng || null,
        timestamp: new Date().toISOString(),
      }),
    });

    setSaving(false);
    if (result) navigate('/');
  };

  return (
    <div className="page">
      <div className="page-header">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', fontSize: '1.5rem' }}
        >
          ←
        </button>
        <h1>Capture Signature</h1>
      </div>

      <div className="page-content">
        {apiError && <p role="alert" style={{ color: 'var(--danger)' }}>{apiError}</p>}
        {/* Signer Info */}
        <div className="card">
          <div className="form-group">
            <label className="form-label">Signer Name</label>
            <Input
              type="text"
              className="form-input"
              required value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Enter name of person signing"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {(['customer', 'driver', 'courier'] as const).map((role) => (
                <button
                  key={role}
                  className={`btn ${signerRole === role ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ flex: 1 }}
                  onClick={() => setSignerRole(role)}
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Signature Pad */}
        <div className="card">
          <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
              Sign below
            </span>
            <button
              onClick={clearSignature}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary)',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>

          <div className="signature-container">
            <canvas
              ref={canvasRef}
              width={window.innerWidth - 64}
              height={200}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        </div>

        {/* GPS Info */}
        <div
          style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: 'var(--gray-500)',
            marginTop: '1rem',
          }}
        >
          {gpsLocation ? (
            <>📍 Location captured: {gpsLocation.lat.toFixed(4)}, {gpsLocation.lng.toFixed(4)}</>
          ) : (
            <>📍 Getting location...</>
          )}
        </div>

        {/* Terms */}
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--gray-500)',
            textAlign: 'center',
            marginTop: '1rem',
          }}
        >
          By signing, you confirm receipt of the goods in good condition.
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="bottom-actions">
        <button
          className="btn btn-secondary"
          style={{ flex: 1 }}
          onClick={() => navigate(-1)}
        >
          Cancel
        </button>
        <button
          className="btn btn-success"
          style={{ flex: 2 }}
          onClick={handleSubmit}
          disabled={saving || !hasSignature || !signerName}
        >
          {saving ? 'Saving...' : '✓ Confirm Delivery'}
        </button>
      </div>
    </div>
  );
}
